import {useEffect, useState} from "react";
import {Alert} from "@mui/material";
import VLFModal from "@/components/Modal";
import VLFSelectNew from "@/components/Input/SelectNew";
import {CustomFootballImportAPI} from "@/api/customFootballImport";

export default function OwnClubLink({clubId, seasonId, close, onLinked}) {
	const [data, setData] = useState(null),
		[targets, setTargets] = useState({}),
		[review, setReview] = useState(null),
		[busy, setBusy] = useState(false),
		[error, setError] = useState("");
	useEffect(() => {
		let active = true;
		setBusy(true);
		CustomFootballImportAPI.clubPreview(clubId, seasonId)
			.then((value) => {
				if (active) {
					setData(value);
					setTargets(
						Object.fromEntries(value.rosters.map((r) => [r.id, null])),
					);
				}
			})
			.catch((e) => {
				if (active)
					setError(
						e.response?.data?.error || "Impossibile leggere le rose del club.",
					);
			})
			.finally(() => {
				if (active) setBusy(false);
			});
		return () => {
			active = false;
		};
	}, [clubId, seasonId]);
	const confirm = async () => {
		setBusy(true);
		setError("");
		try {
			const body = {seasonId, rosters: targets};
			if (!review)
				setReview(await CustomFootballImportAPI.linkPreview(clubId, body));
			else {
				const result = await CustomFootballImportAPI.link(clubId, body);
				onLinked(result);
			}
		} catch (e) {
			setReview(null);
			setError(e.response?.data?.error || "Collegamento non riuscito.");
		} finally {
			setBusy(false);
		}
	};
	return (
		<VLFModal
			open
			title="Collega al mio club"
			maxWidth="md"
			close={() => !busy && close()}
			confirm={confirm}
			disabled={busy || !data?.rosters?.length || data?.alreadyLinked}
			confirmLabel={review ? "Conferma collegamento" : "Verifica collegamento"}
			content={
				<>
					{error && <Alert severity="error">{error}</Alert>}
					{busy && <p>Verifica in corso…</p>}
					{data && (
						<>
							<p>
								Collega <strong>{data.sourceClub.name}</strong> al tuo club{" "}
								<strong>{data.options.ownClub?.name}</strong>, nella stagione
								selezionata.
							</p>
							<p>
								Partite, iscrizioni e giocatori saranno collegati alle rose
								scelte. Le anagrafiche e gli eventi restano gli stessi. Le
								abilitazioni sulle nuove rose si assegnano dalla gestione
								utenti.
							</p>
							{data.alreadyLinked && (
								<Alert severity="info">
									Questo club è già collegato al tuo club nella stagione.
								</Alert>
							)}
							{data.rosters.map((r) => (
								<div key={r.id} className="row align-items-center mb-3">
									<div className="col-md-5">
										<strong>{r.name}</strong>
										<div>
											{r.players} {r.players === 1 ? "giocatore" : "giocatori"}
										</div>
									</div>
									<div className="col-md-7">
										<VLFSelectNew
											label="Rosa interna di destinazione"
											isClearable={false}
											options={[
												{
													value: "new",
													label: "Mantieni come nuova rosa interna",
												},
												...data.options.internalRosters.map((t) => ({
													value: t.id,
													label: t.name,
												})),
											]}
											value={
												targets[r.id]
													? {
															value: targets[r.id],
															label: data.options.internalRosters.find(
																(t) => t.id === targets[r.id],
															)?.name,
														}
													: {
															value: "new",
															label: "Mantieni come nuova rosa interna",
														}
											}
											onChange={(option) => {
												setTargets((prev) => ({
													...prev,
													[r.id]:
														option?.value && option.value !== "new"
															? option.value
															: null,
												}));
												setReview(null);
											}}
											editable={!busy}
										/>
									</div>
								</div>
							))}
							{review && (
								<Alert severity="warning">
									Conferma: {review.rosters} rose, {review.players} giocatori,{" "}
									{review.matches} partite e {review.events} eventi collegati al
									tuo club. Le appartenenze già presenti nelle rose di
									destinazione saranno conservate.
								</Alert>
							)}
						</>
					)}
				</>
			}
		/>
	);
}
